/**
 * THE FIELD BUG, IN NUMBERS — pure (round 8 task 2).
 *
 * On an iPhone Home-Screen PWA, once the software keyboard has been up once,
 * iOS can leave the LAYOUT viewport shifted relative to the VISUAL one until
 * the next real scroll. Every `position: fixed` overlay is measured against
 * the layout viewport, so the map bar, the sheet, the veil and the toast all
 * end up sitting however many pixels the two disagree by — her field report's
 * shot 1 measured that gap at 346px — above where the visual viewport's
 * bottom, and therefore the user's thumb, actually is.
 *
 * `window.visualViewport` reports the truth (`height`, `offsetTop`) the
 * layout viewport (`window.innerHeight`) is not re-measuring on its own.
 * `bottomShift` turns the two into one number of pixels to push a
 * bottom-fixed element DOWN by so its bottom meets the visual viewport's
 * bottom again; `keyboardUp` answers a different question — is something
 * (the keyboard, almost always) actually occluding the bottom of the screen
 * right now, as opposed to merely stale.
 *
 * Both take a plain object rather than `window.visualViewport` itself so this
 * file never touches `window` and stays runnable in vitest's node
 * environment — `viewportGlue.ts` is where the DOM is read.
 */

export interface ViewportSample {
  innerHeight: number;
  vvHeight: number;
  vvOffsetTop: number;
  /**
   * `visualViewport.scale`; absent reads as 1. A pinch-zoom also shrinks the
   * visual viewport (at 2× its height halves), and without this both numbers
   * below would read a zoom as a keyboard — final review of round 8.
   */
  scale?: number;
}

/** A pinch-zoom: the visual viewport is a window over a zoomed page, not a stale one. */
function zoomed(s: ViewportSample): boolean {
  return (s.scale ?? 1) > 1;
}

/** px to push a bottom-fixed element DOWN so its bottom meets the visual viewport's bottom. 0 when nothing is stale. */
export function bottomShift(s: ViewportSample): number {
  if (s.vvHeight <= 0) return 0;
  // Zoomed in, fixed chrome is laid out against the layout viewport and
  // magnified with it; the visual viewport's offset describes where the
  // surveyor is looking, not a disagreement to correct.
  if (zoomed(s)) return 0;
  return Math.round(s.vvOffsetTop + s.vvHeight - s.innerHeight);
}

/** The keyboard (or any occlusion) is up when the visual viewport is meaningfully shorter than the window. */
export function keyboardUp(s: ViewportSample, threshold = 120): boolean {
  if (zoomed(s)) return false;
  return s.innerHeight - s.vvHeight > threshold;
}
